import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, Search, Mail, Calendar, KeyRound, Trash2, X, Loader2, Copy, Plus, CheckCircle2, Clock, UserCog } from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function UsersPage() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null); // attendee id being managed

  const load = useCallback((query = "") => {
    api.get(`/admin/attendees${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then((r) => setUsers(r.data))
      .catch(() => setUsers([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(q), 350);
    return () => clearTimeout(t);
  }, [q, load]);

  return (
    <div className="p-8" data-testid="users-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black tracking-tighter flex items-center gap-2"><Users className="w-7 h-7 text-[#6D28B5]" /> Users</h1>
          <p className="text-[#4B5563] mt-1">Attendee accounts that have signed up. Manage access, reset passwords, and more.</p>
        </div>
      </div>

      <div className="relative mb-5 max-w-sm">
        <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or email"
          data-testid="users-search"
          className="w-full border border-[#0A0A0A] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#6D28B5]"
        />
      </div>

      {users === null ? (
        <div className="text-[#9CA3AF]">Loading…</div>
      ) : users.length === 0 ? (
        <div className="border border-dashed border-[#9CA3AF] bg-white p-16 text-center">
          <p className="text-[#4B5563] font-medium">{q ? "No users match your search." : "No user accounts yet."}</p>
        </div>
      ) : (
        <div className="bg-white border border-[#0A0A0A]">
          <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#E5E7EB] text-xs uppercase tracking-wider font-bold text-[#4B5563]">
            <div className="col-span-4">Name</div>
            <div className="col-span-4">Email</div>
            <div className="col-span-2">Joined</div>
            <div className="col-span-1 text-center">Regs</div>
            <div className="col-span-1"></div>
          </div>
          {users.map((u) => (
            <div key={u.id} className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-[#E5E7EB] last:border-0 text-sm items-center" data-testid={`user-row-${u.id}`}>
              <div className="col-span-4 font-semibold truncate">{u.name || <span className="text-[#9CA3AF]">—</span>}</div>
              <div className="col-span-4 text-[#4B5563] truncate flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {u.email}</div>
              <div className="col-span-2 text-[#4B5563] text-xs">{fmt(u.created_at)}</div>
              <div className="col-span-1 text-center font-semibold">{u.registration_count}</div>
              <div className="col-span-1 flex justify-end">
                <button onClick={() => setSelected(u.id)} data-testid={`manage-user-${u.id}`} className="inline-flex items-center gap-1.5 border border-[#0A0A0A] hover:bg-[#F3F4F6] px-3 py-1.5 text-xs font-semibold">
                  <UserCog className="w-3.5 h-3.5" /> Manage
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <ManageUserModal attendeeId={selected} onClose={() => setSelected(null)} onChanged={() => load(q)} />
      )}
    </div>
  );
}

function ManageUserModal({ attendeeId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [webinars, setWebinars] = useState([]);
  const [grantId, setGrantId] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/attendees/${attendeeId}`).then((r) => {
      setData(r.data);
      setName(r.data.name || "");
      setEmail(r.data.email || "");
    }).catch(() => toast.error("Could not load this user"));
  }, [attendeeId]);

  useEffect(() => {
    load();
    api.get("/admin/webinars").then((r) => setWebinars(r.data || [])).catch(() => {});
  }, [load]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/attendees/${attendeeId}`, { name, email });
      toast.success("User updated");
      load(); onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update");
    }
    setSaving(false);
  };

  const sendReset = async () => {
    setBusy("reset");
    try {
      await api.post(`/admin/attendees/${attendeeId}/send-reset`);
      toast.success("Password reset email sent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send reset");
    }
    setBusy("");
  };

  const remove = async () => {
    if (!window.confirm("Delete this user account permanently? This cannot be undone.")) return;
    setBusy("delete");
    try {
      await api.delete(`/admin/attendees/${attendeeId}`);
      toast.success("Account deleted");
      onChanged(); onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete");
      setBusy("");
    }
  };

  const grant = async () => {
    if (!grantId) return;
    setBusy("grant");
    try {
      const { data: res } = await api.post(`/admin/attendees/${attendeeId}/grant`, { webinar_id: grantId });
      toast.success(res.already ? "Access confirmed (already registered)" : "Access granted");
      setGrantId("");
      load(); onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not grant access");
    }
    setBusy("");
  };

  const removeReg = async (registrantId) => {
    if (!window.confirm("Remove this user from the experience?")) return;
    try {
      await api.delete(`/admin/attendees/${attendeeId}/registrations/${registrantId}`);
      toast.success("Removed from experience");
      load(); onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove");
    }
  };

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Join link copied"); } catch { toast.error("Couldn't copy"); }
  };

  const registeredIds = new Set((data?.registrations || []).map((r) => r.webinar_id));
  const grantable = webinars.filter((w) => !registeredIds.has(w.id));

  return (
    <div className="fixed inset-0 z-[9999] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="manage-user-modal">
      <div className="bg-white w-full max-w-2xl my-8 border border-[#0A0A0A] hard-shadow" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
          <h2 className="text-lg font-bold tracking-tight">Manage user</h2>
          <button onClick={onClose} data-testid="close-manage-modal" className="text-[#6B7280] hover:text-[#0A0A0A]"><X className="w-5 h-5" /></button>
        </div>

        {!data ? (
          <div className="p-10 text-center text-[#9CA3AF]"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : (
          <div className="p-6 space-y-7">
            {/* Profile */}
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-[#4B5563] mb-3">Account details</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#6B7280]">Name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} data-testid="manage-name-input" className="w-full border border-[#0A0A0A] px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-[#6D28B5]" />
                </div>
                <div>
                  <label className="text-xs text-[#6B7280]">Email</label>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="manage-email-input" className="w-full border border-[#0A0A0A] px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-[#6D28B5]" />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <button onClick={saveProfile} disabled={saving} data-testid="manage-save-btn" className="bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-4 py-2 text-sm font-semibold flex items-center gap-1.5">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
                </button>
                <button onClick={sendReset} disabled={busy === "reset"} data-testid="manage-reset-btn" className="border border-[#0A0A0A] hover:bg-[#F3F4F6] disabled:opacity-50 px-4 py-2 text-sm font-semibold flex items-center gap-1.5">
                  {busy === "reset" ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Send password reset
                </button>
                <button onClick={remove} disabled={busy === "delete"} data-testid="manage-delete-btn" className="text-[#FF3B30] border border-[#FF3B30] hover:bg-red-50 disabled:opacity-50 px-4 py-2 text-sm font-semibold flex items-center gap-1.5 ml-auto">
                  {busy === "delete" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete account
                </button>
              </div>
            </div>

            {/* Access / registrations */}
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-[#4B5563] mb-3">Experience access ({data.registrations.length})</div>
              {data.registrations.length === 0 ? (
                <p className="text-sm text-[#9CA3AF] mb-3">Not registered for any experiences yet.</p>
              ) : (
                <div className="border border-[#E5E7EB] divide-y divide-[#E5E7EB] mb-3">
                  {data.registrations.map((r) => (
                    <div key={r.registrant_id} className="flex items-center gap-3 px-3 py-2.5 text-sm" data-testid={`reg-${r.registrant_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{r.title}</div>
                        <div className="text-xs text-[#6B7280] flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {fmtTime(r.session_start)}</div>
                      </div>
                      {r.paid ? (
                        <span className="inline-flex items-center gap-1 text-[#10B981] font-semibold text-xs shrink-0"><CheckCircle2 className="w-3.5 h-3.5" /> {r.payment_status === "free" ? "Access" : "Paid"}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#F59E0B] font-semibold text-xs shrink-0"><Clock className="w-3.5 h-3.5" /> Pending</span>
                      )}
                      {r.paid && (
                        <button onClick={() => copyLink(r.join_url)} title="Copy join link" data-testid={`copy-join-${r.registrant_id}`} className="p-1.5 border border-[#E5E7EB] hover:bg-[#F3F4F6] text-[#4B5563] shrink-0"><Copy className="w-3.5 h-3.5" /></button>
                      )}
                      <button onClick={() => removeReg(r.registrant_id)} title="Remove from experience" data-testid={`remove-reg-${r.registrant_id}`} className="p-1.5 text-[#FF3B30] hover:bg-red-50 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <select value={grantId} onChange={(e) => setGrantId(e.target.value)} data-testid="grant-select" className="flex-1 border border-[#0A0A0A] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6D28B5] bg-white">
                  <option value="">Grant access to an experience…</option>
                  {grantable.map((w) => (
                    <option key={w.id} value={w.id}>{w.title}</option>
                  ))}
                </select>
                <button onClick={grant} disabled={!grantId || busy === "grant"} data-testid="grant-btn" className="bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-4 py-2 text-sm font-semibold flex items-center gap-1.5 shrink-0">
                  {busy === "grant" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Grant
                </button>
              </div>
              <p className="text-xs text-[#9CA3AF] mt-2">Granting access comps a free spot so the user can join and watch, no payment required.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
